import { apiResponseMiddleware } from "@/middleware/api-response";
import toast from "react-hot-toast";
import axios, { AxiosResponse } from "axios";

import { FormType } from "@/types/form/form";

import { api } from "./axios-instance";

const API_ENDPOINT = "/api/Form";
const PUBLIC_FORM_API_URL = import.meta.env.VITE_FORM_API_URL as string;
const publicApi = axios.create({ baseURL: PUBLIC_FORM_API_URL });
export const getAllForms = async ({
  page,
  size,
  search
}: {
  page: number;
  size: number;
  search: string
}) =>
  await apiResponseMiddleware<FormType[]>(
    api.get(`${API_ENDPOINT}/all`, {
      params: {
        pageSize: size,
        pageNumber: page,
        search: search || ""
      }
    }),
    () => { },
    {
      showToast: false
    }
  );

export const getForm = async (id: string | number) => {
  const response = await apiResponseMiddleware<FormType>(
    api.get(`${API_ENDPOINT}/${id}`),
    () => { },
    {
      showToast: false
    }
  );

  return response?.data;
};

export const getFormSize = async (id: string | number) => {
  const response = await apiResponseMiddleware<FormType>(
    api.get(`${API_ENDPOINT}/${id}/Size`),
    () => { },
    {
      showToast: false
    }
  );

  return response?.data;
};
export const createForm = async (form: FormData) => {
  return await apiResponseMiddleware<FormType>(
    api.post(`${API_ENDPOINT}/create`, form),
    () => {
      toast.success("فرم با موفقیت ساخته شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: true
    }
  );
};

export const editForm = async (form: FormData) => {
  return await apiResponseMiddleware<FormType>(
    api.post(`${API_ENDPOINT}/${form.get("id")}/update`, form),
    () => {
      toast.success("فرم با موفقیت ویرایش شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: true
    }
  );
};

export const deleteForm = async (formId: number) => {
  return await apiResponseMiddleware<FormType>(
    api.post(`${API_ENDPOINT}/remove`, JSON.stringify(formId), {
      headers: {
        "Content-Type": "application/json"
      }
    }),
    () => {
      toast.success("فرم با موفقیت حذف شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: false
    }
  );
};

export const insertHtmlContent = async (formId: number, content: string) => {
  return await apiResponseMiddleware<FormType>(
    api.post(
      `${API_ENDPOINT}/${formId}/insertHtmlContent`,
      JSON.stringify(content),
      {
        headers: {
          "Content-Type": "application/json"
        }
      }
    ),
    () => {
      toast.success("فرم با موفقیت ذخیره شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: true
    }
  );
};

export const updateFormEntities = async (
  formId: number,
  entities: number[]
) => {
  return await apiResponseMiddleware(
    api.post(`${API_ENDPOINT}/entities`, entities, {
      params: {
        formId
      }
    }),
    () => {
      toast.success("جداول با موفقیت ثبت شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: true
    }
  );
};

export const uploadImage = async (data: FormData) => {
  const response = await apiResponseMiddleware<{ imageUrl: string }>(
    api.post(`${API_ENDPOINT}/uploadImage`, data),
    () => {
      toast.success("عکس با موفقیت ارسال شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: true
    }
  );

  return response?.data;
};

export const uploadFile = async (
  file: File,
  allowedExtensions?: string
): Promise<{ fileUrl: string; fileName: string } | undefined> => {
  const formData = new FormData();
  formData.append("file", file);
  if (allowedExtensions) {
    formData.append("allowedExtensions", allowedExtensions);
  }

  const response = await apiResponseMiddleware<{
    fileUrl: string;
    fileName: string;
  }>(
    api.post(`${API_ENDPOINT}/uploadFile`, formData),
    () => {
      toast.success("فایل با موفقیت ارسال شد", {
        id: "api-middleware"
      });
    },
    {
      showToast: true
    }
  );

  return response?.data;
};

export const getFormPreview = async (formId: number) => {
  const result = await apiResponseMiddleware<FormType>(
    api.post(`${API_ENDPOINT}/preview`, null, {
      params: {
        formId
      }
    }),
    () => { },
    { showToast: false }
  );
  return result.data;
};

export const getFormPreviewByWorkflowUser = async (
  workflowUserId: string,
  tableSearch:
    | {
      id: string;
      searchElement: string;
      searchValue: string;
    }[]
    | null = null,

  tablePagination:
    | { id: string; pageNumber: string; pageSize: number }[]
    | null = null,

  tableSort:
    | {
      id: string;
      sortElement: string;
      sortDirection: "asc" | "desc";
    }[]
    | null = null,

  formInputs:
    | {
      tableId: number;
      lable: string;
    }[]
    | null = null,

  exportRowCount?: number,
  exportAllRows = false
) => {

  const uniqueFormInputs =
    formInputs == null
      ? []
      : Array.from(
        new Map(
          formInputs.map(item => [
            `${item.tableId}_${item.lable}`,
            item,
          ])
        ).values()
      );

  return await apiResponseMiddleware<FormType>(
    api.post(
      `${API_ENDPOINT}/previewByWorkflowUserId`,
      {
        tableSearches: tableSearch,
        tablePagination,
        tableSorts: tableSort,
        workflowUserId,
        FormInputs: uniqueFormInputs,
        exportRowCount: exportRowCount ?? null,
        exportAllRows,
      }
    ),
    () => { },
    { showToast: false }
  );
};

export const getFormNameByWorkflowUser = async (workflowUserId: string) => {
  const name = await apiResponseMiddleware<string>(
    api.get(`${API_ENDPOINT}/formName`, {
      params: { workflowUserId }
    }),
    () => { },
    { showToast: false }
  );
  return name.data;
};

export const processSelectTag = async (request: {
  htmlBody: string;
  userId?: number;
  formInputs?: { tableId: number; lable: string; value?: string }[];
}) => {
  return await apiResponseMiddleware<string>(
    api.post(`${API_ENDPOINT}/processSelectTag`, {
      HtmlBody: request.htmlBody,
      UserId: request.userId ?? 0,
      FormInputs: request.formInputs ?? []
    }),
    () => { },
    { showToast: false }
  );
};

/**
 * Submit form data; WHY: Backend requires `formData` in body and `FormInputs` in query.
 */
export const saveFormData = async (
  workflowUserId: string,
  activeRoleId: number,
  data: { id: number; content: string; group?: string }[],
  formInputs?: { tableId: number; lable: string; value?: string }[]
) => {

  const uniqueFormInputs = Array.from(
    new Map(
      (formInputs ?? [])
        // حذف مقادیر خالی
        .filter(fi => fi.value != null && fi.value.toString().trim() !== "")
        // حذف آیتم‌های تکراری
        .map(fi => [
          `${fi.tableId}_${fi.lable}_${fi.value}`,
          fi
        ])
    ).values()
  );

  console.log("[api/saveFormData] request", {
    workflowUserId,
    activeRoleId,
    dataLength: data?.length ?? 0,
    formInputsBefore: formInputs?.length ?? 0,
    formInputsAfter: uniqueFormInputs.length
  });

  const queryParams: Record<string, any> = {
    workflowUserId,
    activeRoleId
  };

  uniqueFormInputs.forEach((fi, index) => {
    queryParams[`FormInputs[${index}].tableId`] = fi.tableId;
    queryParams[`FormInputs[${index}].lable`] = fi.lable;
    queryParams[`FormInputs[${index}].value`] = fi.value;
  });

  return await apiResponseMiddleware(
    api.post(
      `${API_ENDPOINT}/saveFormData`,
      data,
      {
        params: queryParams,
        headers: {
          "Content-Type": "application/json"
        }
      }
    ),
    () => { },
    {
      showToast: false
    }
  );
};

export type TableExportFormat =
  | "xlsx"
  | "csv"
  | "pdf"
  | "docx"
  | "json"
  | "xml"
  | "txt"
  | "html";

export interface TableExportPayload {
  tableId?: string;
  title?: string;
  headers: string[];
  rows: string[][];
  accessControlConfig?: string;
  selectedColumns?: string[];
  exportRowCount?: number;
}

export interface ExportTablesRequest {
  format: TableExportFormat;
  fileName?: string;
  tables: TableExportPayload[];
}

export const exportFormTables = async (
  payload: ExportTablesRequest
): Promise<AxiosResponse<Blob>> => {
  return await api.post(`${API_ENDPOINT}/exportTableData`, payload, {
    responseType: "blob"
  });
};

export type TableShareMode =
  | "permanent"
  | "expiring"
  | "multi-use"
  | "internal";

export interface CreateTableShareLinkRequest {
  workflowUserId: string;
  mode: TableShareMode;
  expirationTimeSpan?: string;
  maxUseCount?: number;
  tables: TableExportPayload[];
}

export interface CreateTableShareFileRequest extends CreateTableShareLinkRequest {
  format: TableExportFormat;
  fileName?: string;
}

export interface TableShareResponse {
  shareCode: string;
  mode: string;
  shareUrl: string;
  token?: string;
  expiresAtUtc?: string;
  maxUseCount?: number;
}

export interface TableShareFileResponse extends TableShareResponse {
  fileUrl: string;
  fileName?: string;
}

export interface TableShareViewResponse {
  shareCode: string;
  mode: string;
  expiresAtUtc?: string;
  maxUseCount?: number;
  usedCount: number;
  tables: TableExportPayload[];
}

export const createTableShareLink = async (
  payload: CreateTableShareLinkRequest
) => {
  const response = await apiResponseMiddleware<TableShareResponse>(
    api.post(`${API_ENDPOINT}/share/createLink`, payload),
    () => { },
    { showToast: false }
  );

  return response?.data;
};

export const createTableShareFile = async (
  payload: CreateTableShareFileRequest
) => {
  const response = await apiResponseMiddleware<TableShareFileResponse>(
    api.post(`${API_ENDPOINT}/share/createFile`, payload),
    () => { },
    { showToast: false }
  );

  return response?.data;
};

export const getSharedTableData = async (
  shareCode: string,
  token?: string
) => {
  const response = await apiResponseMiddleware<TableShareViewResponse>(
    publicApi.get(`${API_ENDPOINT}/share/table/${shareCode}`, {
      params: token ? { token } : undefined
    }),
    () => { },
    { showToast: false }
  );

  return response?.data;
};
